import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import DashboardSkeleton from '../../../components/common/skeletons/DashboardSkeleton';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { fetchManufacturerDashboard } from '../../../services/Http/ManufacturerApi';
import { useCurrency } from '../../../context/CurrencyProvider';
import {
    MdInventory,
    MdStore,
    MdLocalShipping,
    MdTrendingUp,
    MdTrendingDown,
    MdAssignmentTurnedIn,
    MdSchedule,
    MdVerifiedUser,
    MdPeopleAlt,
    MdShowChart,
    MdWarning,
    MdEqualizer
} from 'react-icons/md';
import styled from 'styled-components';

const metricConfig = [
    { key: 'total_skus', label: 'ManufacturerDashboard.totalSkus', icon: MdInventory, color: 'users', format: 'number' },
    { key: 'active_retailers_count', label: 'ManufacturerDashboard.activeRetailers', icon: MdStore, color: 'venues', format: 'number' },
    { key: 'active_distributors_count', label: 'ManufacturerDashboard.activeDistributors', icon: MdLocalShipping, color: 'transactions', format: 'number' },
    { key: 'sell_in_value', label: 'ManufacturerDashboard.sellIn', icon: MdTrendingDown, color: 'revenueValue', format: 'currency' },
    { key: 'sell_in_quantity', label: 'ManufacturerDashboard.sellInQuantity', icon: MdInventory, color: 'soldItems', format: 'number' },
    { key: 'sell_in_transactions_count', label: 'ManufacturerDashboard.sellInTransactions', icon: MdEqualizer, color: 'neutral', format: 'number' },
    { key: 'sell_through_value', label: 'ManufacturerDashboard.sellThrough', icon: MdTrendingUp, color: 'totalVolume', format: 'currency' },
    { key: 'sell_through_quantity', label: 'ManufacturerDashboard.sellThroughQuantity', icon: MdInventory, color: 'users', format: 'number' },
    { key: 'sell_through_transactions_count', label: 'ManufacturerDashboard.sellThroughTransactions', icon: MdEqualizer, color: 'info', format: 'number' },
    { key: 'sell_through_rate', label: 'ManufacturerDashboard.sellThroughRate', icon: MdShowChart, color: 'campaigns', format: 'percent' },
    { key: 'unique_buyers_count', label: 'ManufacturerDashboard.uniqueBuyers', icon: MdPeopleAlt, color: 'users', format: 'number' },
    { key: 'warranty_activation_rate', label: 'ManufacturerDashboard.warrantyActivation', icon: MdVerifiedUser, color: 'manufacturer', format: 'percent' },
    { key: 'open_service_tickets_count', label: 'ManufacturerDashboard.openTickets', icon: MdAssignmentTurnedIn, color: 'danger', format: 'number' },
    { key: 'returns_rate', label: 'ManufacturerDashboard.returnsRate', icon: MdWarning, color: 'warning', format: 'percent' },
    { key: 'campaigns_running_count', label: 'ManufacturerDashboard.campaignsRunning', icon: MdLocalShipping, color: 'campaigns', format: 'number' },
    { key: 'avg_time_to_repair', label: 'ManufacturerDashboard.avgTtr', icon: MdSchedule, color: 'neutral', format: 'days' },
];

const ManufacturerDashboard = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await fetchManufacturerDashboard();
                setData(response);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Format metric value with currency support from context
    const formatMetricValue = useCallback((value, formatType = 'number') => {
        const metric = typeof value === 'object' && value !== null ? value : { value };
        const rawValue = metric.value;

        if (rawValue === null || rawValue === undefined) {
            return '0';
        }

        const numeric = Number(rawValue);
        if (Number.isNaN(numeric)) {
            return rawValue;
        }

        switch (formatType) {
            case 'currency':
                return formatAmount(
                    numeric,
                    metric.localAmount,
                    metric.localCurrencyId,
                    metric.localCurrencyCode
                );
            case 'percent':
                return `${numeric.toFixed(2)}%`;
            case 'days':
                return `${numeric.toFixed(2)} d`;
            default:
                if (Number.isInteger(numeric)) {
                    return numeric.toLocaleString();
                }
                return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }, [formatAmount]);

    // Render list with currency formatting
    const renderList = useCallback((items = [], labelKey, valueFormat = 'currency') => {
        if (!items || items.length === 0) {
            return <MutedText>–</MutedText>;
        }

        return (
            <InsightList>
                {items.map((item, idx) => (
                    <InsightListItem key={`${labelKey}-${idx}`}>
                        <ListLabel>{item[labelKey] || item}</ListLabel>
                        {(item.value !== undefined || item.transactions !== undefined) && (
                            <ListValue>
                                {item.value !== undefined && formatMetricValue({
                                    value: item.value,
                                    localAmount: item.value_local,
                                    localCurrencyId: item.seller_currency_id ?? item.currency_id,
                                    localCurrencyCode: item.seller_currency_code ?? item.currency_code,
                                }, valueFormat)}
                                {item.transactions !== undefined && (
                                    <span className="tx-count">({item.transactions} tx)</span>
                                )}
                            </ListValue>
                        )}
                    </InsightListItem>
                ))}
            </InsightList>
        );
    }, [formatMetricValue]);



    if (error) {
        return <ErrorMessage>{t('ManufacturerDashboard.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('ManufacturerDashboard.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                {loading ? (
                    <DashboardSkeleton />
                ) : (
                    <>
                        <StatsGrid>
                            {metricConfig.map(({ key, label, icon: Icon, color, format }) => (
                                <StatCard
                                    key={key}
                                    icon={<Icon className="icon" />}
                                    title={t(label)}
                                    value={formatMetricValue({
                                        value: data?.[key],
                                        localAmount: key === 'sell_in_value' ? data?.sell_in_value_local : key === 'sell_through_value' ? data?.sell_through_value_local : null,
                                        localCurrencyId: key === 'sell_in_value' ? data?.sell_in_currency_id : key === 'sell_through_value' ? data?.sell_through_currency_id : null,
                                    }, format)}
                                    color={color}
                                />
                            ))}
                        </StatsGrid>
                        <InsightsGrid>
                            <InsightCard>
                                <InsightTitle>{t('ManufacturerDashboard.topSkus')}</InsightTitle>
                                {renderList(data?.top_skus, 'sku')}
                            </InsightCard>
                            <InsightCard>
                                <InsightTitle>{t('ManufacturerDashboard.topRetailers')}</InsightTitle>
                                {renderList(data?.top_retailers, 'retailer')}
                            </InsightCard>
                            <InsightCard>
                                <InsightTitle>{t('ManufacturerDashboard.topUserRegions')}</InsightTitle>
                                {renderList(data?.top_regions, 'region')}
                            </InsightCard>
                            <InsightCard>
                                <InsightTitle>{t('ManufacturerDashboard.topRetailerRegions')}</InsightTitle>
                                {renderList(data?.top_retailer_regions, 'region')}
                            </InsightCard>
                            <InsightCard>
                                <InsightTitle>{t('ManufacturerDashboard.topBuyers')}</InsightTitle>
                                {renderList(data?.top_buyers, 'buyer')}
                            </InsightCard>
                            <InsightCard>
                                <InsightTitle>{t('ManufacturerDashboard.actions')}</InsightTitle>
                                <ActionList>
                                    {(data?.action_items || []).map((item, idx) => (
                                        <ActionItem key={idx}>{item}</ActionItem>
                                    ))}
                                    {(!data?.action_items || data.action_items.length === 0) && (
                                        <MutedText>{t('ManufacturerDashboard.noActions')}</MutedText>
                                    )}
                                </ActionList>
                            </InsightCard>
                        </InsightsGrid>
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
};

const InsightsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: ${({ theme }) => theme.spacing.medium || '1.25rem'};
    margin-top: ${({ theme }) => theme.spacing.large || '1.875rem'};
`;

const InsightCard = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: ${({ theme }) => theme.spacing.medium || '1.25rem'};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const InsightTitle = styled.h4`
    margin-bottom: ${({ theme }) => theme.spacing.small || '0.625rem'};
    color: ${({ theme }) => theme.colors.brand || '#821692'};
`;

const MutedText = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    margin: 0;
`;

const InsightList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const InsightListItem = styled.li`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 8px;
    padding: 0.625rem 0.75rem;
    background: rgba(130, 22, 146, 0.03);
    display: flex;
    flex-direction: column;
`;

const ListLabel = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary || '#333'};
`;

const ListValue = styled.span`
    color: ${({ theme }) => theme.colors.revenue || '#4caf50'};
    font-size: 0.95rem;
    margin-top: 0.25rem;

    .tx-count {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        margin-left: 0.375rem;
    }
`;

const ActionList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const ActionItem = styled.li`
    border-left: 4px solid ${({ theme }) => theme.colors.warning || '#ffc107'};
    background: rgba(255, 193, 7, 0.1);
    padding: 0.625rem 0.75rem;
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.textPrimary || '#333'};
`;

export default ManufacturerDashboard;
